// Simple Scroll Indicator Module
// Lightweight version that only checks on load and resize

class SimpleScrollIndicator {
  constructor() {
    this.init();
  }

  init() {
    // Check once on load
    this.checkOverflow();
    
    // Check on resize with throttling
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.checkOverflow();
      }, 200);
    });
    
    // Check when layout changes (like upload button click)
    this.observeLayoutChanges();
  }

  observeLayoutChanges() {
    // Use MutationObserver to watch for class changes on the main container
    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          // Check if the class change affects layout
          const target = mutation.target;
          const classList = target.classList;
          
          // Check for layout-affecting classes
          if (classList.contains('upload-aside-active') || 
              classList.contains('main-custom-split') ||
              classList.contains('aside-custom-split') ||
              classList.contains('col-12') ||
              classList.contains('col-md-') ||
              classList.contains('col-lg-') ||
              classList.contains('col-xl-')) {
            shouldCheck = true;
          }
        }
      });
      
      if (shouldCheck) {
        // Small delay to ensure CSS has been applied
        setTimeout(() => {
          this.checkOverflow();
        }, 100);
      }
    });

    // Observe the main container, body, and any container elements for class changes
    const elementsToObserve = [
      document.querySelector('main'),
      document.body,
      document.querySelector('.container'),
      document.querySelector('.row')
    ].filter(el => el); // Remove null elements
    
    elementsToObserve.forEach(element => {
      observer.observe(element, {
        attributes: true,
        attributeFilter: ['class']
      });
    });
    
    // Also listen for custom events that might indicate layout changes
    document.addEventListener('layout-changed', () => {
      setTimeout(() => {
        this.checkOverflow();
      }, 100);
    });
  }

  checkOverflow() {
    const cards = document.querySelectorAll('main .card');
    
    cards.forEach(card => {
      const cardBody = card.querySelector('.card-body');
      if (!cardBody) return;
      
      // Check if the card body has horizontal overflow
      const hasHorizontalOverflow = cardBody.scrollWidth > cardBody.clientWidth;
      
      if (hasHorizontalOverflow) {
        card.classList.add('has-scrollable-content');
        this.setupScrollListeners(card, cardBody);
      } else {
        card.classList.remove('has-scrollable-content');
        this.removeScrollListeners(card, cardBody);
      }
    });
  }
  
  setupScrollListeners(card, cardBody) {
    // Remove existing listeners to prevent duplicates
    this.removeScrollListeners(card, cardBody);
    
    const updateScrollState = () => {
      const scrollLeft = cardBody.scrollLeft;
      const maxScrollLeft = cardBody.scrollWidth - cardBody.clientWidth;
      
      // Update scroll position classes
      card.classList.toggle('can-scroll-left', scrollLeft > 0);
      card.classList.toggle('can-scroll-right', scrollLeft < maxScrollLeft);
    };
    
    // Initial state
    updateScrollState();
    
    // Add scroll listener
    cardBody.addEventListener('scroll', updateScrollState);
    
    // Store reference for cleanup
    cardBody._scrollListener = updateScrollState;
  }
  
  removeScrollListeners(card, cardBody) {
    if (cardBody._scrollListener) {
      cardBody.removeEventListener('scroll', cardBody._scrollListener);
      delete cardBody._scrollListener;
    }
    
    // Remove scroll state classes
    card.classList.remove('can-scroll-left', 'can-scroll-right');
  }
  
  // Public method to manually trigger overflow check
  static triggerCheck() {
    const instance = window.scrollIndicatorInstance;
    if (instance) {
      instance.checkOverflow();
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.scrollIndicatorInstance = new SimpleScrollIndicator();
});

// For Turbo navigation
document.addEventListener('turbo:load', () => {
  if (window.scrollIndicatorInstance) {
    // Clean up previous instance if it exists
    window.scrollIndicatorInstance = null;
  }
  window.scrollIndicatorInstance = new SimpleScrollIndicator();
});

export default SimpleScrollIndicator; 